/**
 * NsectionB16Page
 * B.16.方法論原則（A6.4-FORM-METH-002 Section 16. Methodologies principles）
 * 六頁籤：16.1~16.6 各一個富文本欄位
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB16Service, { NsectionB16Detail } from '../services/nsectionB16Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';

interface TabDef { key: string; field: string; labelZh: string; labelEn: string; }

const TABS: TabDef[] = [
  { key: '16.1', field: 'encouraging_ambition', labelZh: '16.1 鼓勵隨時間提高雄心', labelEn: '16.1 Encouraging ambition over time' },
  { key: '16.2', field: 'equitable_sharing', labelZh: '16.2 促進減緩效益公平分享', labelEn: '16.2 Equitable sharing' },
  { key: '16.3', field: 'broad_participation', labelZh: '16.3 鼓勵廣泛參與', labelEn: '16.3 Broad participation' },
  { key: '16.4', field: 'attributability', labelZh: '16.4 減排量可歸因性', labelEn: '16.4 Attributability' },
  { key: '16.5', field: 'perverse_incentives', labelZh: '16.5 潛在不當激勵', labelEn: '16.5 Perverse incentives' },
  { key: '16.6', field: 'rebound_effects', labelZh: '16.6 反彈效應', labelEn: '16.6 Rebound effects' },
];

const NsectionB16Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB16Detail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('16.1');

  const [fields, setFields] = useState<Record<string, string>>({
    encouraging_ambition: '', equitable_sharing: '', broad_participation: '',
    attributability: '', perverse_incentives: '', rebound_effects: '',
  });

  useEffect(() => { if (!hasInitialized.current && !permissionLoading) { validateSession('NsectionB16Page'); hasInitialized.current = true; } }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) { setData(null); setFields({ encouraging_ambition: '', equitable_sharing: '', broad_participation: '', attributability: '', perverse_incentives: '', rebound_effects: '' }); return; }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const r = await nsectionB16Service.getByProposal(selectedProposalId);
        if (r) {
          setData(r);
          setFields({
            encouraging_ambition: r.encouraging_ambition || '',
            equitable_sharing: r.equitable_sharing || '',
            broad_participation: r.broad_participation || '',
            attributability: r.attributability || '',
            perverse_incentives: r.perverse_incentives || '',
            rebound_effects: r.rebound_effects || '',
          });
        } else {
          setData(null);
          setFields({ encouraging_ambition: '', equitable_sharing: '', broad_participation: '', attributability: '', perverse_incentives: '', rebound_effects: '' });
        }
      } catch (err: any) { if (err.response?.status !== 404) setError(err.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed')); }
      finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  const handleSave = async () => {
    if (!selectedProposalId) return;
    const currentTab = TABS.find(t => t.key === activeTab);
    if (!currentTab) return;
    try {
      setLoading(true);
      const r = await nsectionB16Service.update(selectedProposalId, { [currentTab.field]: fields[currentTab.field] });
      setData(r); toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) { toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Failed')); }
    finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b16', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('nsection_b16', 'update');

  const tabBtn = (a: boolean): React.CSSProperties => ({ padding: '10px 16px', border: 'none', borderBottom: a ? '3px solid #4A90E2' : '3px solid transparent', background: a ? '#f0f7ff' : 'transparent', color: a ? '#4A90E2' : '#666', fontWeight: a ? 600 : 400, cursor: 'pointer', fontSize: 13, marginBottom: -2 });

  return (
    <div className="page-container">
      <div className="page-header"><FunctionPageHeader funcCode="nsection_b16" /></div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 16 }}>{isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}</div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          <div style={{ display: 'flex', borderBottom: '2px solid #e0e0e0', marginBottom: 16, flexWrap: 'wrap' }}>
            {TABS.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={tabBtn(activeTab === tab.key)}>
                {isZh ? tab.labelZh : tab.labelEn}
              </button>
            ))}
          </div>

          {TABS.map(tab => {
            if (tab.key !== activeTab) return null;
            return (
              <div key={tab.key} style={{ width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#333', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                  {isZh ? tab.labelZh : tab.labelEn}
                </div>
                {canEdit ? (
                  <RichTextEditor
                    value={fields[tab.field]}
                    onChange={v => setFields(prev => ({ ...prev, [tab.field]: v }))}
                    placeholder={isZh ? '請輸入內容...' : 'Enter content...'}
                    minHeight="250px"
                  />
                ) : (
                  <div style={{ padding: 16, fontSize: 15, color: '#333', lineHeight: 1.8, minHeight: 250 }}
                    dangerouslySetInnerHTML={{ __html: fields[tab.field] || '-' }} />
                )}
                {canEdit && (
                  <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
                    <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                      {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NsectionB16Page;
